import * as React from 'react';
import './Billing.scss';
import { TK } from '../../store/translations/translationKeys';
import { Translate, useTranslations } from '../../store/translations/hooks';
import { ButtonGroup, Button, Modal, ModalBody, ModalHeader, ListGroup, ListGroupItem } from 'reactstrap';
import StyledTable from '../../components/StyledTable';
import moment, { Moment } from 'moment';
import { ExpenseAttachment, ExpenseData, RecurringConfig, RecurringUnit } from '../../models';
import { useDispatch, useSelector } from 'react-redux';
import { getMyUser } from '../../store/session/selectors';
import { useEntitiesResult } from '../../store/entities/hooks';
import { ExpenseEntity } from '../../store/entities/state';
import { isLoadingEntities } from '../../store/entities/selectors';
import { entityCreating, loadEntities, openEntity } from '../../store/entities/actions';
import { exportExpensesZipAsync, getExpensesAsync } from '../../ajax/requests';
import Typography from '../../components/Typography';
import { hide } from '../../shared/style';
import { formatEuros } from '../../shared/string';
import { BillingResponsiveBar } from './BillingResponsiveBar';
import { CSVLink } from 'react-csv';
import { DateRangeSelector, DateRangeSelectorState } from '../../components/DateRangeSelector';
import Add from '@material-ui/icons/Add';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { Tooltip } from '@mui/material';
import { AutocompleteTextField, SwitchField } from '../../components/Forms/fields';
import { TotalsGrid, StatCard, CardLabel, CardValue, CardSubValue } from './BillingStatCards';
import { useBillingGroupBy } from '../../hooks/useBillingGroupBy';
import { cookiesOptions, getRecurringDates, Guid } from '../../shared/utils';
import { useCookies } from 'react-cookie';

interface Coiso {
    values: Record<string, number>
    counts: Record<string, number>
}

interface BillItem {
    label: string,
    data: ExpenseData[],
    values: Record<string, number>,
    counts: Record<string, number>,
}

const getTotal = (data: ExpenseData[]): {value: number, count: number} => data.reduce((acc, {payment}) => {
    return {
        value: acc.value + payment.value,
        count: acc.count + 1,
    };
}, {value: 0, count: 0})

const getTotalsByExpenseName = (expenses: ExpenseData[]) => expenses.reduce<Coiso>((acc, {name, type, payment}) => {
    return {
        values: {
            ...acc.values,
            [`${name} (${type})`]: (acc.values[name]??0) + payment.value,
        },
        counts: {
            ...acc.counts,
            [`${name} (${type})`]: (acc.counts[name]??0) + 1,
        },
    }
}, {values: {}, counts: {}})

const getTotalsByExpenseType = (expenses: ExpenseData[]) => expenses.reduce<Coiso>((acc, e) => {
    return {
        values: {
            ...acc.values,
            [e.type ?? 'Unset']: (acc.values[e.type ?? 'Unset']??0) + e.payment.value,
        },
        counts: {
            ...acc.counts,
            [e.type ?? 'Unset']: (acc.counts[e.type ?? 'Unset']??0) + 1,
        },
    }
}, {values: {}, counts: {}})

const getTotalsByPaymentType = (expenses: ExpenseData[]) => {
    return expenses.reduce<Coiso>((acc, {payment: p}) => ({
        values: {
            ...acc.values,
            [p.type]: (acc.values[p.type]??0) + p.value,
        },
        counts: {
            ...acc.counts,
            [p.type]: (acc.counts[p.type]??0) + 1,
        },
    }), {values: {}, counts: {}})
}

function* getDataByDay(
    t: Translate<TK>,
    expenses: ExpenseData[],
    splitBy: 'byExpenseName'|'byExpenseType'|'byPaymentType',
    groupBy: 'byDay'|'byWeek'|'byMonth',
    window: DateRangeSelectorState,
): Generator<BillItem> {
    const dateFormat = groupBy === 'byMonth' ? 'YYYY-MM' : 'YYYY-MM-DD'

    const perDay = expenses.reduce<Record<string, ExpenseData[]>>((acc, data) => {
        const day = groupBy === 'byWeek' ? moment.utc(data.payment.date).startOf('isoWeek').format(dateFormat)
            : groupBy === 'byMonth' ? moment.utc(data.payment.date).startOf('month').format(dateFormat)
            : data.payment.date.substring(0, 10)
        return {
            ...acc,
            [day]: acc[day] ? [...acc[day], data] : [data],
        };
    }, {})

    const from = groupBy === 'byWeek' ? moment.utc(window.from).startOf('isoWeek')
            : groupBy === 'byMonth' ? moment.utc(window.from).startOf('month')
            : moment.utc(window.from)

    const to = moment.utc(window.to).add(1, 'day')
    let current = from

    while (current < to) {
        const day = current.format(dateFormat)
        const data = perDay[day] ?? []
        const total = getTotal(data)

        const splitTotals = splitBy === 'byExpenseName' ? getTotalsByExpenseName(data) 
            : splitBy === 'byExpenseType' ? getTotalsByExpenseType(data)
            : getTotalsByPaymentType(data)

        yield {
            label: day,
            data,
            values: {
                [t(TK.total)]: total.value,
                ...splitTotals.values,
            },
            counts: {
                [t(TK.total)]: total.count,
                ...splitTotals.counts,
            },
        };

        current = current.add(1, groupBy === 'byWeek' ? 'weeks' : groupBy === 'byMonth' ? 'months' : 'days')
    }
}

export const getRecurringPayments = (expense: ExpenseData, from: Moment, to: Moment): ExpenseData[] => {
    const {recurring} = expense;
    if (!recurring) return [expense];
    return getRecurringDates(recurring, expense.payment?.date ?? from, from, to).map(date => ({
        ...expense,
        payment: {
            ...expense.payment,
            date: date.toISOString(),
        },
    }))
}

const getRecurringDescription = (config: RecurringConfig, t: Translate<TK>): string => 
    config.unit === RecurringUnit.Days ? `${config.value} ${t(TK.days).toLocaleLowerCase()}` 
    : config.unit === RecurringUnit.Weeks ? `${config.value} ${t(TK.weeks).toLocaleLowerCase()}` 
    : config.unit === RecurringUnit.Months ? `${config.value} ${t(TK.months).toLocaleLowerCase()}` 
    : '';


export const getExpenseDescription = (expense: ExpenseData, t: Translate<TK>): string => {
    if (expense.recurring) {
        const period = getRecurringDescription(expense.recurring, t);
        return `${expense.payment.date.substring(0, 10)} - ${expense.recurring.endDate ? expense.recurring.endDate.substring(0, 10) : t(TK.forever)} (every ${period})`;
    } else {
        return expense.payment.date.substring(0, 10);
    }
}

const renderExpenseAttachments = (attachments: ExpenseAttachment[]) => {
    if (!attachments.length) return '-';

    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-XXS)', flexWrap: 'wrap'}}>
            {attachments.map((attachment, idx) => {
                const icon = (
                    <AttachFileIcon style={{fontSize: '1rem', color: 'var(--text-muted)'}} />
                );

                if (attachment.url) {
                    return (
                        <Tooltip key={`${attachment.fileId}-${idx}`} title={attachment.fileName} arrow>
                            <a
                                href={attachment.url}
                                download={attachment.fileName}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(ev) => ev.stopPropagation()}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: 'inherit',
                                    textDecoration: 'none',
                                }}
                            >
                                {icon}
                            </a>
                        </Tooltip>
                    );
                }

                return (
                    <Tooltip key={`${attachment.fileId}-${idx}`} title={attachment.fileName} arrow>
                        <span style={{display: 'inline-flex', alignItems: 'center'}}>
                            {icon}
                        </span>
                    </Tooltip>
                );
            })}
        </div>
    );
};

const Expenses: React.FC = () => {
    const t = useTranslations()
    const dispatch = useDispatch()
    const _expenses = useEntitiesResult<ExpenseEntity>('expenses')
    const isLoading = useSelector(isLoadingEntities('expenses'))

    const [cookies, setCookie] = useCookies()
    const [shownExpenseKeys, setShownExpenseKeys] = React.useState<Set<string> | null>(null);
    const [dates, setDates] = React.useState<DateRangeSelectorState | undefined>()
    const [splitBy, setSplitBy] = React.useState<'byExpenseName'|'byExpenseType'|'byPaymentType'>('byExpenseType')
    const [groupBy, setGroupBy] = useBillingGroupBy()
    const [dontGroup, setDontGroup] = React.useState<boolean>(!!cookies['BILLING_DONT_GROUP'])
    const [filters, setFilters] = React.useState<string[]>([])
    const [invertFilter, setInvertFilter] = React.useState(false)
    const [withoutVAT, setWithoutVAT] = React.useState(false)

    const expensesRaw = !filters.length ? _expenses : _expenses.filter(p => {
        const matches = filters.includes(p.name) || filters.includes(p.type) || filters.includes(p.payment.type)
        return invertFilter ? !matches : matches
    })

    const expensesPayments = React.useMemo(() => expensesRaw.flatMap<ExpenseData>(e => e.recurring && dates 
            ? getRecurringPayments(e, moment.utc(dates.from), moment.utc(dates.to)) 
            : [e])
        , [expensesRaw, dates])

    React.useEffect(() => {
        if (dates) {
            const diff = moment.utc(dates.to).diff(moment.utc(dates.from), 'months')
            setGroupBy(diff <= 1 ? 'byDay' : 'byMonth')
            dispatch(loadEntities('expenses', () => getExpensesAsync(dates.from, dates.to), 'NewResult', expensesRaw.map(x => x.id)))
        }
    }, [dates])

    const hasVAT = React.useMemo(() => expensesPayments.some(e => e.payment.vat), [expensesPayments])

    const totalVAT = React.useMemo(() =>
        expensesPayments.reduce((acc, e) => acc + (e.payment.vat ? e.payment.value - e.payment.value / (1 + e.payment.vat) : 0), 0)
    , [expensesPayments])

    const processedExpenses = React.useMemo(() =>
        withoutVAT
            ? expensesPayments.map(e => e.payment.vat ? { ...e, payment: { ...e.payment, value: e.payment.value / (1 + e.payment.vat) } } : e)
            : expensesPayments
    , [expensesPayments, withoutVAT])

    const expensesToShow = React.useMemo(
        () => shownExpenseKeys ? processedExpenses.filter(e => shownExpenseKeys.has(`${e.id}|${e.payment.date}`)) : [],
        [shownExpenseKeys, processedExpenses],
    );
    const showExpenses = React.useCallback((expenses: ExpenseData[]) => {
        setShownExpenseKeys(expenses.length ? new Set(expenses.map(e => `${e.id}|${e.payment.date}`)) : null);
    }, []);

    const dataByDay: BillItem[] = React.useMemo(
        () => dates && [...getDataByDay(t, processedExpenses, splitBy, groupBy, dates)] || [],
        [splitBy, processedExpenses, dates]);

    const total = React.useMemo(() => getTotal(processedExpenses), [processedExpenses])

    const totals = React.useMemo(() => ([
        getTotalsByExpenseName(processedExpenses),
        getTotalsByExpenseType(processedExpenses),
        getTotalsByPaymentType(processedExpenses),
    ]), [processedExpenses])

    const splitTotals = splitBy === 'byExpenseName' ? totals[0] : splitBy === 'byExpenseType' ? totals[1] : totals[2]

    const splitVATTotals = React.useMemo(() =>
        expensesPayments.reduce<Record<string, number>>((acc, e) => {
            const vatAmount = e.payment.vat ? e.payment.value - e.payment.value / (1 + e.payment.vat) : 0
            if (!vatAmount) return acc
            const key = splitBy === 'byExpenseName' ? `${e.name} (${e.type})`
                : splitBy === 'byExpenseType' ? (e.type ?? 'Unset')
                : e.payment.type
            return { ...acc, [key]: (acc[key] ?? 0) + vatAmount }
        }, {})
    , [expensesPayments, splitBy])

    const columns = React.useMemo(() => dataByDay.length ? [...new Set(dataByDay.flatMap(d => Object.keys(d.values)))] : [], [dataByDay]);

    const dataAll = React.useMemo(() => processedExpenses
        .map((e, i) => ({
            id: e.id,
            name: e.name,
            type: e.type,
            date: e.payment.date.substring(0, 10),
            endDate: e.recurring?.endDate?.substring(0, 10),
            period: e.recurring ? getRecurringDescription(e.recurring, t) : undefined,
            totalValue: e.payment.value,
            attachments: e.attachments,
            vatAmount: expensesPayments[i].payment.vat
                ? expensesPayments[i].payment.value - expensesPayments[i].payment.value / (1 + expensesPayments[i].payment.vat)
                : undefined,
            sortIndex: i,
        }))
        .sort((a, b) => {
            const dateDiff = moment(a.date, 'YYYY-MM-DD').valueOf() - moment(b.date, 'YYYY-MM-DD').valueOf();
            return dateDiff !== 0 ? dateDiff : a.sortIndex - b.sortIndex;
        })
        .map(({sortIndex, ...expense}) => expense)
    , [processedExpenses, expensesPayments, t])

    const currentUsername = useSelector(getMyUser)?.username

    const handleOpenCreateModal = () => {
        dispatch(entityCreating('expenses', {
            id: Guid.newGuid(),
            name: '',
            payment: {value: 0, type: '', date: new Date().toISOString()},
            createdBy: currentUsername,
        }))
    }

    const handleOpenEditModal = (id: string) => {
        const expense = expensesRaw.find(e => e.id === id)
        if (!expense) return;
        dispatch(openEntity('expenses', expense.id, () => Promise.resolve(expense)))
    }

    return (
        <div className='billingContainer'>
            {isLoading && <Typography style={{position: 'fixed', bottom: 0, left: 0, backgroundColor: 'white'}}>{t(TK.loading)}...</Typography>}

            <div className="dateRangePanel">
                <DateRangeSelector onRangeChanged={setDates}/>

                <Button
                    color="success"
                    onClick={handleOpenCreateModal}
                    style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)'}}
                >
                    <Add />
                </Button>
            </div>

            <div className="billTypePanel">
                <ButtonGroup >
                    <Button outline active={splitBy === 'byExpenseType'} onClick={() => setSplitBy('byExpenseType')}>{t(TK.byExpenseType)}</Button>
                    <Button outline active={splitBy === 'byExpenseName'} onClick={() => setSplitBy('byExpenseName')}>{t(TK.byExpenseName)}</Button>
                    <Button outline active={splitBy === 'byPaymentType'} onClick={() => setSplitBy('byPaymentType')}>{t(TK.byPaymentType)}</Button>
                </ButtonGroup>
                <ButtonGroup >
                    <Button outline color="info" active={groupBy === 'byDay'} onClick={() => setGroupBy('byDay')}>{t(TK.byDay)}</Button>
                    <Button outline color="info" active={groupBy === 'byWeek'} onClick={() => setGroupBy('byWeek')}>{t(TK.byWeek)}</Button>
                    <Button outline color="info" active={groupBy === 'byMonth'} onClick={() => setGroupBy('byMonth')}>{t(TK.byMonth)}</Button>
                </ButtonGroup>
                <AutocompleteTextField
                    options={[...new Set(_expenses.flatMap(d => [d.name, d.type, d.payment.type]))]}
                    values={filters}
                    onValuesChanged={setFilters}
                    multiSelect
                    placeholder={`${t(TK.filter)}...`}
                />
            </div>

            { isLoading ? <Typography>{t(TK.loading)}...</Typography>
                : <>
                    <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'var(--spacer-M)', padding: 'var(--spacer-XS) 0', borderBottom: 'var(--border-thin) solid var(--appLigthGrey)', marginBottom: 'var(--spacer-S)'}}>
                        {filters.length > 0 && (
                            <SwitchField label={t(TK.invertFilterResult)} value={invertFilter} onChange={setInvertFilter} />
                        )}
                        {hasVAT && (
                            <SwitchField label={t(TK.withoutVAT)} value={withoutVAT} onChange={setWithoutVAT} />
                        )}
                    </div>

                    <TotalsGrid>
                        <StatCard accent="var(--danger-color)">
                            <CardLabel>{t(TK.total)}</CardLabel>
                            <CardValue textColor="var(--danger-color)">{formatEuros(total.value)}</CardValue>
                            <CardSubValue>{total.count} {t(TK.expenses).toLowerCase()}</CardSubValue>
                            {hasVAT && !withoutVAT && totalVAT > 0 && (
                                <CardSubValue>{t(TK.vat)}: {formatEuros(totalVAT)}</CardSubValue>
                            )}
                        </StatCard>
                        {Object.entries(splitTotals.values).map(([key, value]) => (
                            <StatCard
                                key={key}
                                accent="var(--appLigthGrey)"
                                style={{cursor: 'pointer'}}
                                onClick={() => showExpenses(processedExpenses.filter(e =>
                                    splitBy === 'byExpenseName' ? `${e.name} (${e.type})` === key
                                    : splitBy === 'byExpenseType' ? (e.type ?? 'Unset') === key
                                    : e.payment.type === key
                                ))}
                            >
                                <CardLabel>{key}</CardLabel>
                                <CardValue textColor="var(--text-body)">{formatEuros(value)}</CardValue>
                                <CardSubValue>{splitTotals.counts[key]} {t(TK.expenses).toLowerCase()}</CardSubValue>
                                {hasVAT && !withoutVAT && splitVATTotals[key] > 0 && (
                                    <CardSubValue>{t(TK.vat)}: {formatEuros(splitVATTotals[key])}</CardSubValue>
                                )}
                            </StatCard>
                        ))}
                    </TotalsGrid>
                    <BillingResponsiveBar
                        data={dataByDay.map(({label, values}) => ({label, ...values}))}
                        keys={
                            splitBy === 'byExpenseName' ? Object.keys(totals[0].values) :
                            splitBy === 'byExpenseType' ? Object.keys(totals[1].values) :
                            splitBy === 'byPaymentType' ? Object.keys(totals[2].values) :
                            [t(TK.total)]
                        }
                        onClick={(id, item) => splitBy === 'byExpenseName' ? showExpenses(dataByDay.find(d => d.label === item.label)?.data.filter(d => id.startsWith(d.name)) ?? []) 
                            : splitBy === 'byExpenseType' ? showExpenses(dataByDay.find(d => d.label === item.label)?.data.filter(d => d.type === id) ?? [])
                            : splitBy === 'byPaymentType' ? showExpenses(dataByDay.find(d => d.label === item.label)?.data.filter(d => d.payment.type === id) ?? [])
                            : undefined}
                    />

                    <div style={{display: 'flex', gap: 'var(--spacer-XS)', justifyContent: 'end', position: 'relative', top: '-30px'}}>
                        <SwitchField 
                            style={{display: 'inline'}}
                            label={t(TK.dontGroup)}
                            value={dontGroup}
                            id='dont-group'
                            onChange={e => {
                                const val = !!e
                                setDontGroup(val)
                                setCookie('BILLING_DONT_GROUP', val ? '1' : '', cookiesOptions)
                            }}
                        />
                    </div>

                    <div className="tableContainer" style={hide(!dontGroup)}>
                        <StyledTable>
                            <thead>
                                <tr>
                                    <th>{t(TK.name)}</th>
                                    <th>{t(TK.type)}</th>
                                    <th>{t(TK.date)}</th>
                                    <th>{t(TK.endDate)}</th>
                                    <th>{t(TK.period)}</th>
                                    <th>{t(TK.attachments)}</th>
                                    <th>{t(TK.total)}</th>
                                    {hasVAT && !withoutVAT && <th>{t(TK.vat)}</th>}
                                </tr>
                            </thead>
                            <tbody>
                            {dataAll.map((e) => (
                                <tr key={`${e.id}-${e.date}`} onClick={() => handleOpenEditModal(e.id)} style={{cursor: 'pointer'}}>
                                    <td>{e.name}</td>
                                    <td>{e.type}</td>
                                    <td>{e.date}</td>
                                    <td>{e.period ? e.endDate ?? t(TK.never) : '-'}</td>
                                    <td>{e.period ?? '-'}</td>
                                    <td>{renderExpenseAttachments(e.attachments ?? [])}</td>
                                    <td>{formatEuros(e.totalValue)}</td>
                                    {hasVAT && !withoutVAT && <td style={{color: 'var(--text-muted)'}}>{e.vatAmount != null ? formatEuros(e.vatAmount) : '-'}</td>}
                                </tr>
                            ))}
                            </tbody>
                        </StyledTable>
                    </div>

                    <div className="billingTable" style={hide(dontGroup)}>
                        <StyledTable>
                            <thead>
                                <tr>
                                    <th/>
                                    { columns.map(column => <th key={column}>{column}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                { dataByDay.map((item, i) =>
                                    <tr key={`${item.label}-${i}`} onClick={() => showExpenses(item.data)} style={{cursor: 'pointer'}}>
                                        <td>{item.label}</td>
                                        { columns.map(column =>
                                            <td key={column}>{`${formatEuros(item.values[column] ?? 0)} (${item.counts[column] ?? 0})`}</td>
                                        )}
                                    </tr>
                                )}
                            </tbody>
                        </StyledTable>
                    </div>

                    <div style={{marginTop: 'var(--spacer-M)', display: 'flex', gap: 'var(--spacer-S)', alignItems: 'center'}}>
                        <CSVLink
                            data={dataAll}
                            separator=';'
                            target='_blank'
                            filename='expenses-export.csv'
                        >
                            {t(TK.exportData)}
                        </CSVLink>
                        {dates && (
                            <Button
                                color="link"
                                onClick={() => exportExpensesZipAsync(dates.from, dates.to)}
                            >
                                {t(TK.exportAttachments)}
                            </Button>
                        )}
                    </div>
                </>
            }

            <Modal size='lg' isOpen={!!expensesToShow.length} toggle={() => showExpenses([])}>
                <ModalHeader toggle={() =>  showExpenses([])}>{t(TK.expenses)}</ModalHeader>
                <ModalBody>
                    <ListGroup>
                        {expensesToShow.map(e => (
                            <ListGroupItem
                                key={e.id}
                                style={{cursor: 'pointer'}}
                            >
                                <div
                                    onClick={() => handleOpenEditModal(e.id)}
                                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                                >
                                    <div>
                                        <div>
                                            <b>{e.name} ({e.type})</b>
                                        </div>
                                        <div>
                                            {getExpenseDescription(e, t)}
                                        </div>
                                    </div>
                                    <div style={{textAlign: 'right'}}>
                                        <b>{formatEuros(e.payment.value)}</b>
                                        {!!e.payment.vat && (
                                            <div style={{fontSize: '0.78rem', color: 'var(--text-muted)'}}>
                                                {t(TK.vat)}: {formatEuros(e.payment.value - e.payment.value / (1 + e.payment.vat))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {e.attachments && e.attachments.length > 0 && (
                                    <div style={{marginTop: 'var(--spacer-XS)', paddingTop: 'var(--spacer-XS)', borderTop: 'var(--border-thin) solid var(--appLigthGrey)'}}>
                                        <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XXS)'}}>
                                            {e.attachments.map((attachment, idx) => (
                                                <div key={`${attachment.fileId}-${idx}`} style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-XXS)'}}>
                                                    <AttachFileIcon style={{fontSize: '1rem', color: 'var(--text-muted)'}} />
                                                    {attachment.url ? (
                                                        <a
                                                            href={attachment.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(ev) => ev.stopPropagation()}
                                                            style={{
                                                                fontSize: '0.875rem',
                                                                color: 'var(--appBlue)',
                                                                textDecoration: 'none',
                                                            }}
                                                            onMouseEnter={(ev) => ev.currentTarget.style.textDecoration = 'underline'}
                                                            onMouseLeave={(ev) => ev.currentTarget.style.textDecoration = 'none'}
                                                        >
                                                            {attachment.fileName}
                                                        </a>
                                                    ) : (
                                                        <span style={{fontSize: '0.875rem', color: 'var(--text-muted)'}}>
                                                            {attachment.fileName}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </ListGroupItem>
                        ))}
                    </ListGroup>
                </ModalBody>
            </Modal>
        </div>
    );
}

export default Expenses;
